import type { SynthLayer } from "./types";

/**
 * Promoted from `src/audio/lab/recipeValidation.ts`. Safety/fairness
 * constants and the pure validator every recipe (production catalog and
 * QA lab alike) must stay inside. `buildRecipe.ts` enforces the locked
 * ones (SAFE, CHAOS anticipation) directly when constructing a recipe;
 * this module is also what test suites import to check a whole catalog at
 * once.
 */

/** No single recipe layer/envelope may exceed this peak linear gain - real headroom is still left below actual Web Audio clipping (1.0) once the master gain/limiter in `AudioBackend.ts` are applied on top. */
export const MAX_RECIPE_PEAK_GAIN = 0.6;

/** SAFE must stay "shortest and least intense" regardless of voice. */
export const SAFE_MAX_DURATION_MS = 160;
export const SAFE_MAX_PEAK_GAIN = 0.22;

/** CHAOS anticipation's "roughly 150-250ms" window, with a few ms of slack for envelope rounding. */
export const CHAOS_ANTICIPATION_MIN_MS = 145;
export const CHAOS_ANTICIPATION_MAX_MS = 255;

/** Any recipe's total duration must be a short arcade sound, never an accidental multi-second drone. */
export const MAX_RECIPE_DURATION_MS = 1200;

export const MIN_AUDIBLE_FREQ_HZ = 40;
export const MAX_AUDIBLE_FREQ_HZ = 12000;

export const MAX_PAN = 1;
export const MIN_PAN = -1;

export const MAX_FILTER_Q = 20;

/** Cross-voice fairness tolerance for `perceptualLoudness` on the same event id. */
export const CROSS_PALETTE_LOUDNESS_TOLERANCE = 0.12;

function isFiniteNumber(n: number): boolean {
  return typeof n === "number" && Number.isFinite(n);
}

/** General safety-range checks that apply to every recipe regardless of which event it is - duration/pan/loudness/tones/noise/envelope/delayTaps. `tag` is only used to make failures readable (e.g. `"minimalPremium/safe"`). */
export function validateSynthLayer(recipe: SynthLayer, tag: string): string[] {
  const issues: string[] = [];

  if (!isFiniteNumber(recipe.durationMs) || recipe.durationMs <= 0) issues.push(`${tag}: durationMs must be a positive finite number`);
  if (recipe.durationMs > MAX_RECIPE_DURATION_MS) issues.push(`${tag}: durationMs ${recipe.durationMs} exceeds MAX_RECIPE_DURATION_MS`);

  if (!isFiniteNumber(recipe.pan) || recipe.pan < MIN_PAN || recipe.pan > MAX_PAN) issues.push(`${tag}: pan ${recipe.pan} out of [-1, 1]`);

  if (!isFiniteNumber(recipe.perceptualLoudness) || recipe.perceptualLoudness < 0 || recipe.perceptualLoudness > 1) {
    issues.push(`${tag}: perceptualLoudness ${recipe.perceptualLoudness} out of [0, 1]`);
  }

  if (recipe.tones.length === 0) issues.push(`${tag}: must have at least one tone layer`);
  for (const [i, tone] of recipe.tones.entries()) {
    if (!isFiniteNumber(tone.startFreqHz) || tone.startFreqHz < MIN_AUDIBLE_FREQ_HZ || tone.startFreqHz > MAX_AUDIBLE_FREQ_HZ) {
      issues.push(`${tag}: tone[${i}].startFreqHz ${tone.startFreqHz} out of audible range`);
    }
    if (!isFiniteNumber(tone.endFreqHz) || tone.endFreqHz < MIN_AUDIBLE_FREQ_HZ || tone.endFreqHz > MAX_AUDIBLE_FREQ_HZ) {
      issues.push(`${tag}: tone[${i}].endFreqHz ${tone.endFreqHz} out of audible range`);
    }
    if (!isFiniteNumber(tone.mix) || tone.mix < 0 || tone.mix > 1) issues.push(`${tag}: tone[${i}].mix ${tone.mix} out of [0, 1]`);
  }

  if (recipe.noise) {
    const { filterFreqHz, filterQ, mix } = recipe.noise;
    if (!isFiniteNumber(filterFreqHz) || filterFreqHz < MIN_AUDIBLE_FREQ_HZ || filterFreqHz > MAX_AUDIBLE_FREQ_HZ) {
      issues.push(`${tag}: noise.filterFreqHz ${filterFreqHz} out of audible range`);
    }
    if (!isFiniteNumber(filterQ) || filterQ < 0 || filterQ > MAX_FILTER_Q) issues.push(`${tag}: noise.filterQ ${filterQ} out of [0, ${MAX_FILTER_Q}]`);
    if (!isFiniteNumber(mix) || mix < 0 || mix > 1) issues.push(`${tag}: noise.mix ${mix} out of [0, 1]`);
  }

  const env = recipe.envelope;
  for (const [key, value] of Object.entries({ attackMs: env.attackMs, decayMs: env.decayMs, sustainMs: env.sustainMs, releaseMs: env.releaseMs })) {
    if (!isFiniteNumber(value) || value < 0) issues.push(`${tag}: envelope.${key} ${value} must be a non-negative finite number`);
  }
  if (!isFiniteNumber(env.sustainLevel) || env.sustainLevel < 0 || env.sustainLevel > 1) issues.push(`${tag}: envelope.sustainLevel ${env.sustainLevel} out of [0, 1]`);
  if (!isFiniteNumber(env.peakGain) || env.peakGain <= 0 || env.peakGain > MAX_RECIPE_PEAK_GAIN) {
    issues.push(`${tag}: envelope.peakGain ${env.peakGain} out of (0, ${MAX_RECIPE_PEAK_GAIN}]`);
  }

  if (recipe.delayTaps) {
    for (const [i, tap] of recipe.delayTaps.entries()) {
      if (!isFiniteNumber(tap.delayMs) || tap.delayMs < 0) issues.push(`${tag}: delayTaps[${i}].delayMs ${tap.delayMs} must be non-negative`);
      if (!isFiniteNumber(tap.gain) || tap.gain < 0 || tap.gain > 1) issues.push(`${tag}: delayTaps[${i}].gain ${tap.gain} out of [0, 1]`);
    }
  }

  return issues;
}

/** The extra restraint SAFE must satisfy regardless of voice - checked separately from `validateSynthLayer` since only the SAFE event carries this rule. */
export function validateSafeConstraints(recipe: SynthLayer, tag: string): string[] {
  const issues: string[] = [];
  if (recipe.durationMs > SAFE_MAX_DURATION_MS) issues.push(`${tag}: SAFE durationMs ${recipe.durationMs} exceeds SAFE_MAX_DURATION_MS`);
  if (recipe.envelope.peakGain > SAFE_MAX_PEAK_GAIN) issues.push(`${tag}: SAFE peakGain ${recipe.envelope.peakGain} exceeds SAFE_MAX_PEAK_GAIN`);
  return issues;
}

/** CHAOS anticipation's own duration window, checked separately since only that event carries this rule. */
export function validateChaosAnticipationConstraints(recipe: SynthLayer, tag: string): string[] {
  if (recipe.durationMs < CHAOS_ANTICIPATION_MIN_MS || recipe.durationMs > CHAOS_ANTICIPATION_MAX_MS) {
    return [`${tag}: chaosAnticipation durationMs ${recipe.durationMs} outside [${CHAOS_ANTICIPATION_MIN_MS}, ${CHAOS_ANTICIPATION_MAX_MS}]`];
  }
  return [];
}
